import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
// @ts-nocheck
import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
const NAME_PRESETS = ['custom', 'gold', 'rainbow', 'neon', 'fire', 'ice', 'glitch', 'royal'];
const POST_HL_PRESETS = ['custom', 'gold-border', 'silver-border', 'glow-blue', 'glow-purple', 'glow-green', 'ribbon-red', 'shadow-soft'];
const SOURCE_FILE = 'file';
const SOURCE_URL = 'url';

/**
 * User-side modal for submitting a decoration design. Behaviour per type:
 *
 *  - avatar_decoration / cover_decoration   → File upload OR image URL
 *    (user picks one; file upload reuses the admin upload pipeline so the
 *    same size / extension / magic-byte / finfo defenses apply).
 *  - name_decoration / post_highlight_decoration → preset + optional CSS.
 *  - title_decoration → title text + optional color + optional CSS.
 *
 * Submissions land in `status=pending` server-side; the admin sees them
 * in the moderation queue. The submitter sees their own row in their
 * personal scope (the SubmissionScope filter on the catalog lets the
 * creator see their pending submission so they know it was received).
 *
 * Attrs:
 *   - type:  'avatar_decoration' | 'name_decoration' | ...
 *   - onSubmitted?: () => void   (parent panel callback to refresh)
 */
export default class SubmitDecorationModal extends Modal {
  constructor() {
    super(...arguments);
    _defineProperty(this, "busy", false);
    _defineProperty(this, "err", '');
    // Field state — supersets every type's shape. The submit() filters down
    // to the fields the chosen type accepts.
    _defineProperty(this, "name", '');
    _defineProperty(this, "description", '');
    _defineProperty(this, "imageUrl", '');
    _defineProperty(this, "imageFile", null);
    // For avatar/cover, the user picks between uploading a file or providing
    // a remote URL. File goes through the multipart pipeline that the admin
    // upload uses; URL is stored verbatim on the row and rendered as <img src>.
    _defineProperty(this, "source", SOURCE_FILE);
    _defineProperty(this, "preset", 'custom');
    _defineProperty(this, "customCss", '');
    _defineProperty(this, "titleText", '');
    _defineProperty(this, "color", '');
  }
  className() {
    return 'PointSystemSubmitModal Modal--large';
  }
  title() {
    const family = this.familyKey();
    return app.translator.trans('ramon-point-system.forum.submit.title_with', {
      family: app.translator.trans('ramon-point-system.forum.submit.family_' + family)
    });
  }
  familyKey() {
    const type = String(this.attrs.type || '');
    return type === 'post_highlight_decoration' ? 'post_hl' : type.replace(/_decoration$/, '');
  }
  content() {
    const t = (k, v) => app.translator.trans('ramon-point-system.forum.submit.' + k, v);
    const type = String(this.attrs.type || '');
    const isImage = type === 'avatar_decoration' || type === 'cover_decoration';
    const isName = type === 'name_decoration';
    const isTitle = type === 'title_decoration';
    const isPostHl = type === 'post_highlight_decoration';
    return m("div", {
      className: "Modal-body PointSystemSubmitModal-body"
    }, m("p", {
      className: "helpText"
    }, t('intro')), m("div", {
      className: "Form-group"
    }, m("label", null, t('name_label')), m("input", {
      type: "text",
      className: "FormControl",
      value: this.name,
      maxLength: 100,
      placeholder: t('name_placeholder'),
      oninput: e => this.name = e.target.value,
      autofocus: true
    })), m("div", {
      className: "Form-group"
    }, m("label", null, t('description_label')), m("input", {
      type: "text",
      className: "FormControl",
      value: this.description,
      maxLength: 500,
      oninput: e => this.description = e.target.value
    })), isImage && m("div", null, m("div", {
      className: "Form-group PointSystemSubmitModal-source"
    }, m("label", null, t('source_label')), m("div", {
      className: "PointSystemSubmitModal-sourceToggle"
    }, m("label", {
      className: 'PointSystemSubmitModal-sourceOption ' + (this.source === SOURCE_FILE ? 'is-active' : '')
    }, m("input", {
      type: "radio",
      name: "ps-submit-source",
      value: SOURCE_FILE,
      checked: this.source === SOURCE_FILE,
      onchange: () => {
        this.source = SOURCE_FILE;
      }
    }), m("i", {
      className: "fas fa-upload"
    }), " ", t('source_file')), m("label", {
      className: 'PointSystemSubmitModal-sourceOption ' + (this.source === SOURCE_URL ? 'is-active' : '')
    }, m("input", {
      type: "radio",
      name: "ps-submit-source",
      value: SOURCE_URL,
      checked: this.source === SOURCE_URL,
      onchange: () => {
        this.source = SOURCE_URL;
      }
    }), m("i", {
      className: "fas fa-link"
    }), " ", t('source_url')))), this.source === SOURCE_FILE ? m("div", {
      className: "Form-group"
    }, m("label", null, t('image_file_label')), m("input", {
      type: "file",
      className: "FormControl",
      accept: type === 'avatar_decoration' ? 'image/png,image/gif,image/webp,image/apng' : 'image/png,image/jpeg,image/gif,image/webp,image/apng',
      onchange: e => {
        var _files$, _files;
        const f = (_files$ = (_files = e.target.files) == null ? void 0 : _files[0]) != null ? _files$ : null;
        this.imageFile = f;
      }
    }), m("p", {
      className: "helpText"
    }, t('image_file_help'))) : m("div", {
      className: "Form-group"
    }, m("label", null, t('image_url_label')), m("input", {
      type: "url",
      className: "FormControl",
      value: this.imageUrl,
      placeholder: "https://example.com/frame.png",
      oninput: e => this.imageUrl = e.target.value
    }), m("p", {
      className: "helpText"
    }, t('image_url_help')))), isTitle && m("div", null, m("div", {
      className: "Form-group"
    }, m("label", null, t('title_text_label')), m("input", {
      type: "text",
      className: "FormControl",
      value: this.titleText,
      maxLength: 60,
      placeholder: t('title_text_placeholder'),
      oninput: e => this.titleText = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, t('color_label')), m("input", {
      type: "text",
      className: "FormControl",
      value: this.color,
      placeholder: "#6cc04a",
      maxLength: 24,
      oninput: e => this.color = e.target.value
    }))), (isName || isPostHl) && m("div", {
      className: "Form-group"
    }, m("label", null, t('preset_label')), m("select", {
      className: "FormControl",
      value: this.preset,
      onchange: e => this.preset = e.target.value
    }, (isName ? NAME_PRESETS : POST_HL_PRESETS).map(p => m("option", {
      value: p
    }, p)))), (isName || isPostHl || isTitle) && m("div", {
      className: "Form-group"
    }, m("label", null, t('custom_css_label')), m("textarea", {
      className: "FormControl",
      rows: 6,
      value: this.customCss,
      placeholder: "color: gold;",
      oninput: e => this.customCss = e.target.value
    }), m("p", {
      className: "helpText"
    }, t('custom_css_help'))), this.err && m("p", {
      className: "PointSystemSubmitModal-error"
    }, m("i", {
      className: "fas fa-exclamation-triangle"
    }), " ", this.err), m("div", {
      className: "PointSystemSubmitModal-footer"
    }, m("p", {
      className: "helpText"
    }, m("i", {
      className: "fas fa-info-circle"
    }), " ", t('moderation_notice')), m(Button, {
      className: "Button Button--primary",
      loading: this.busy,
      disabled: !this.canSubmit() || this.busy,
      onclick: () => this.submit()
    }, m("i", {
      className: "fas fa-paper-plane"
    }), " ", t('submit'))));
  }
  canSubmit() {
    if (!this.name.trim()) return false;
    const type = String(this.attrs.type || '');
    if (type === 'avatar_decoration' || type === 'cover_decoration') {
      return this.source === SOURCE_FILE ? !!this.imageFile : !!this.imageUrl.trim();
    }
    if (type === 'title_decoration') {
      return !!this.titleText.trim();
    }
    return true;
  }
  async submit() {
    const type = String(this.attrs.type || '');
    const isImage = type === 'avatar_decoration' || type === 'cover_decoration';
    const apiType = {
      avatar_decoration: 'point-system-avatar-decorations',
      name_decoration: 'point-system-name-decorations',
      cover_decoration: 'point-system-cover-decorations',
      title_decoration: 'point-system-title-decorations',
      post_highlight_decoration: 'point-system-post-highlight-decorations'
    }[type];
    if (!apiType) return;
    this.busy = true;
    this.err = '';
    m.redraw();
    try {
      // For image types with a file source, route through the multipart
      // upload endpoint (server forces status=pending, creator_id=actor,
      // is_enabled=false). For URL source and the other types, use the
      // JSON:API store which already passes through the SubmissionScope
      // and policy checks.
      if (isImage && this.source === SOURCE_FILE && this.imageFile) {
        const apiUrl = (app.forum.attribute('apiUrl') || '/api').replace(/\/+$/, '');
        const uploadPath = type === 'avatar_decoration' ? 'avatar-decoration/upload' : 'cover-decoration/upload';
        const form = new FormData();
        form.append('image', this.imageFile);
        form.append('name', this.name.trim());
        if (this.description.trim()) form.append('description', this.description.trim());
        // No price field — non-manager uploads have price forced to 0 server-side.
        await app.request({
          method: 'POST',
          url: "".concat(apiUrl, "/point-system/").concat(uploadPath),
          serialize: b => b,
          body: form
        });
      } else {
        const attrs = {
          name: this.name.trim(),
          description: this.description.trim() || null
        };
        if (isImage) {
          attrs.imageUrl = this.imageUrl.trim();
        }
        if (type === 'name_decoration' || type === 'post_highlight_decoration') {
          attrs.preset = this.preset;
          attrs.customCss = this.customCss.trim() || null;
        }
        if (type === 'title_decoration') {
          attrs.titleText = this.titleText.trim();
          attrs.color = this.color.trim() || null;
          attrs.customCss = this.customCss.trim() || null;
        }
        await app.store.createRecord(apiType).save(attrs);
      }
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('ramon-point-system.forum.submit.submitted_alert'));
      if (this.attrs.onSubmitted) this.attrs.onSubmitted();
      this.hide();
    } catch (e) {
      var _e$response;
      this.err = (e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail) || 'Submission failed';
    } finally {
      this.busy = false;
      m.redraw();
    }
  }
}
flarum.reg.add('ramon-point-system', 'forum/components/SubmitDecorationModal', SubmitDecorationModal);